import { Box } from '@mui/material';
import { MemoEmptyChart } from 'charts/common/EmptyChart';
import { formatTranslation } from 'intl';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { getDashboardChartAreaSx } from '../theme/muiChartSlotProps';
import {
  CHART_DONUT_DASHBOARD_PIE_MAX_SIZE,
} from '../tokens/muiChartLayout';
import {
  TripletCountDisplay,
  TripletGaugeDisplay,
  TRIPLET_GAUGE_COLORS,
} from './triplet/tripletDisplay';
import {
  INSPECTION_SUMMARY_PARTS,
  toInspectionSummarySeries,
  type InspectionSummaryPart,
} from './triplet/inspectionSummaryParts';

type TripletPartChartViewProps = Readonly<{
  chartId: string;
  part: InspectionSummaryPart;
  values: readonly (number | undefined)[];
}>;

export function TripletPartChartView({
  chartId,
  part,
  values,
}: TripletPartChartViewProps): ReactElement {
  const intl = useIntl();
  const partConfig = INSPECTION_SUMMARY_PARTS.find(
    (entry) => entry.part === part
  );
  const label = partConfig
    ? formatTranslation(intl, partConfig.titleId)
    : '';
  const { inspectionCount, failedFactor, inspectionResult } =
    toInspectionSummarySeries(values);

  const content = (() => {
    switch (part) {
      case 'count':
        return (
          <TripletCountDisplay
            chartId={chartId}
            value={inspectionCount}
            label={label}
            size={CHART_DONUT_DASHBOARD_PIE_MAX_SIZE}
            variant='dashboard'
          />
        );
      case 'failedFactor':
        return (
          <TripletGaugeDisplay
            chartId={chartId}
            gaugeIndex={0}
            value={failedFactor}
            color={TRIPLET_GAUGE_COLORS.failedFactor}
            label={label}
            size={CHART_DONUT_DASHBOARD_PIE_MAX_SIZE}
            variant='dashboard'
          />
        );
      case 'result':
        return (
          <TripletGaugeDisplay
            chartId={chartId}
            gaugeIndex={1}
              value={inspectionResult}
            color={TRIPLET_GAUGE_COLORS.result}
            label={label}
            size={CHART_DONUT_DASHBOARD_PIE_MAX_SIZE}
            variant='dashboard'
          />
        );
      default:
        return null;
    }
  })();

  if (!content) {
    return <MemoEmptyChart variant='dashboard' />;
  }

  return (
    <Box
      sx={{
        ...getDashboardChartAreaSx(),
        alignItems: 'center',
        width: '100%',
      }}
    >
      {content}
    </Box>
  );
}
